/**
 * ponCharacter — THE shared in-stage Pon Pon character for the canvas (Phaser)
 * stages. This is the gumdrop PonPon the plaza shows (`./ponBlob.ts#ponBlobHtml`),
 * rasterised to a Phaser texture from the SAME shared SVG atoms
 * (`ui/character/ponArt.ts`) so the in-stage Pon is pixel-identical to the plaza
 * Pon — no more placeholder circle.
 *
 * It is a self-contained, reusable module: ALL stages (1-0 / 1-1 / 1-1b / 1-4
 * and any future stage) build their Pons through `createPonCharacter` and drive
 * them with `setState` / `setFacing` each frame from the existing
 * server-synced Pon state. **Client-visual only** — it READS state, never writes
 * it; gameplay/collision/protocol are untouched.
 *
 * Animation states (CSS keyframes in `ponpon.css` → Phaser tweens here, matching
 * durations/feel):
 *  - `idle`      — gentle breathe (`pon-breathe`, 2.8s squash/stretch).
 *  - `run`       — bouncy walk (`pon-walk`, 0.42s hop + lean).
 *  - `jump`      — stretched up while rising.
 *  - `fall`      — squashed while descending.
 *  - `absorbing` — channelling the suck: continuous shake + open pulsing mouth
 *                  (`pon-absorbing`).
 *  - `absorbed`  — pinned/caught: steady puffed hold, mouth open (`pon-absorbed`).
 *  - `carrying`  — dragging a crate / a teammate: a planted, slightly-squashed
 *                  trudge (no jump, slow move — mirrors the carry gameplay).
 */

import Phaser from 'phaser';
import type { CostumeId } from '@ponponland/shared';
import { COSTUMES } from '@ponponland/shared';
import { ponBodySvg } from './ponArt.js';
import { PON_STATE_VISUAL, mouthOpen } from './ponState.js';
import type { PonRenderer, PonRendererOptions } from './ponRenderer.js';

// `PonAnimState` + `ponStateFrom` now live in `./ponState.ts` (the single state
// map shared with the plaza). Re-export so existing importers
// (`StageScene.ts`) keep resolving them through `ponCharacter.js`.
export { ponStateFrom } from './ponState.js';
export type { PonAnimState } from './ponState.js';
import type { PonAnimState } from './ponState.js';

/** Texture render resolution (px). The viewBox is 100×100; we render at 2× for
 *  crisp edges, then scale the sprite down to the requested display size. */
const TEX_RES = 200;
/** Fraction of the 100-unit viewBox the gumdrop body occupies (y13→85 ≈ 0.72). */
const BODY_FRACTION = 0.72;

/** Phaser texture key for a (costume, open-mouth) variant. */
function texKey(costume: CostumeId, open: boolean): string {
  return `pon-${costume}-${open ? 'o' : 'c'}`;
}

/**
 * Queue every Pon character texture (all costumes × open/closed mouth) for a
 * scene. Call from the scene's `preload()` so the textures are decoded before
 * any Pon is built — the roster's costumes are known by `create()`, but
 * preloading the full (small) set avoids any async-decode race mid-frame.
 *
 * Idempotent: skips keys Phaser already has (textures persist across scenes).
 */
export function preloadPonCharacters(scene: Phaser.Scene): void {
  for (const c of COSTUMES) {
    for (const open of [false, true]) {
      const key = texKey(c.id, open);
      if (scene.textures.exists(key)) continue;
      const svg = ponBodySvg({
        colour: c.tint,
        costume: c.id,
        // `open` (round) eyes read best at stage scale, matching the plaza Pon.
        eyes: 'open',
        absorbMouth: open,
        size: TEX_RES,
      });
      scene.load.svg(key, svgDataUri(svg), { width: TEX_RES, height: TEX_RES });
    }
  }
}

/**
 * Base64 `data:` URI for an SVG string. Phaser's `load.svg` decodes a data URI
 * with `atob`, so it must be base64 (NOT percent-encoded). `btoa` needs Latin-1;
 * our SVG markup is ASCII (hex colours, ASCII path data), so a direct `btoa` is
 * safe here — but guard non-ASCII via `unescape(encodeURIComponent(...))`.
 */
function svgDataUri(svg: string): string {
  const b64 = btoa(unescape(encodeURIComponent(svg)));
  return 'data:image/svg+xml;base64,' + b64;
}

export interface PonCharacterOptions extends PonRendererOptions {
  scene: Phaser.Scene;
}

/**
 * One in-stage Pon. Owns a Container (added at 0,0; the caller positions it each
 * frame) holding: a ground shadow, the gumdrop sprite, and the name label.
 * Drive it with {@link PonCharacter.setFacing} + {@link PonCharacter.setState}.
 */
export class PonCharacter implements PonRenderer {
  readonly container: Phaser.GameObjects.Container;
  private readonly scene: Phaser.Scene;
  private readonly costume: CostumeId;
  private readonly size: number;
  private readonly sprite: Phaser.GameObjects.Image;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  readonly label: Phaser.GameObjects.Text;

  private facing: 'left' | 'right' = 'right';
  private state: PonAnimState = 'idle';
  /** Base (un-animated) sprite scale magnitude; facing applies the sign. */
  private readonly baseScale: number;
  /** The active state's looping tween (idle/run/absorbing), if any. */
  private stateTween?: Phaser.Tweens.Tween;

  constructor(opts: PonCharacterOptions) {
    const { scene, costume, size, name } = opts;
    this.scene = scene;
    this.costume = costume;
    this.size = size;

    const r = size / 2;
    this.shadow = scene.add.ellipse(0, r, r * 1.6, r * 0.45, 0x1c2b5e, 0.18);

    // Sprite origin at its FEET-ish (cy≈0.9 of the viewBox is the body base) so
    // squash/stretch pivots from the ground like the CSS `transform-origin:
    // bottom center`, and the container's (0,0) sits at the body centre (where
    // the old circle sat) — keeps positioning identical to the placeholder.
    this.sprite = scene.add.image(0, 0, texKey(costume, false)).setOrigin(0.5, 0.9);
    // The gumdrop body spans viewBox y13→85 (~0.72 of the 100 height), rendered
    // into the TEX_RES texture; scale so that body height ≈ `size` (matching the
    // old placeholder circle's diameter), keeping in-stage proportions identical.
    this.baseScale = size / (TEX_RES * BODY_FRACTION);
    this.sprite.setScale(this.baseScale);
    // Origin is the body base (cy0.9); nudge the sprite down so the body CENTRE
    // sits on the container origin (where the old circle centre sat).
    this.sprite.setY(r * 0.62);

    this.label = scene.add
      .text(0, -r - 10, name, {
        fontFamily: 'Fredoka, sans-serif',
        fontSize: '13px',
        color: '#3a2a16',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 1);

    this.container = scene.add.container(0, 0, [this.shadow, this.sprite, this.label]);
    this.applyState('idle', true);
  }

  /** Mirror the gumdrop to face left/right (label stays unmirrored). */
  setFacing(facing: 'left' | 'right'): void {
    if (this.facing === facing) return;
    this.facing = facing;
    // Re-apply the current state so its tween targets the new facing sign.
    this.applyState(this.state, true);
  }

  /** Switch animation state (no-op if unchanged). */
  setState(state: PonAnimState): void {
    if (this.state === state) return;
    this.applyState(state, false);
  }

  private signedScaleX(mag: number): number {
    return this.facing === 'left' ? -mag : mag;
  }

  private applyState(state: PonAnimState, force: boolean): void {
    if (!force && this.state === state) return;
    this.state = state;
    this.stateTween?.stop();
    this.stateTween = undefined;
    this.scene.tweens.killTweensOf(this.sprite);

    // Swap to the open-mouth texture for absorbing/absorbed, closed otherwise.
    const key = texKey(this.costume, mouthOpen(state));
    if (this.sprite.texture.key !== key) this.sprite.setTexture(key);

    const b = this.baseScale;
    const baseY = this.size * 0.31;
    const lean = this.facing === 'left' ? -1 : 1; // mirror the lean direction with facing
    // The state's recipe (durations/scale/rotate/lift), authored from ponpon.css
    // so the canvas tween reads identical to the plaza CSS keyframe.
    const v = PON_STATE_VISUAL[state];
    const peakX = this.signedScaleX(b * v.squash[0]);
    const peakY = b * v.squash[1];

    // Reset transform before the state's tween takes over.
    this.sprite.setScale(this.signedScaleX(b), b).setAngle(0).setX(0).setY(baseY);

    switch (state) {
      case 'idle':
        // pon-breathe (2.8s): a slow yoyo breathe to the peak squash.
        this.stateTween = this.scene.tweens.add({
          targets: this.sprite,
          scaleX: peakX,
          scaleY: peakY,
          duration: v.durationMs / 2,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.InOut',
        });
        break;
      case 'run':
        // pon-walk (0.42s): a bouncy hop + lean sweep, amplitudes from the recipe.
        this.stateTween = this.scene.tweens.add({
          targets: this.sprite,
          y: { from: baseY, to: baseY + v.liftPx },
          scaleY: { from: b, to: peakY },
          angle: { from: -v.rotateDeg * lean, to: v.rotateDeg * lean },
          duration: v.durationMs / 2,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.InOut',
        });
        break;
      case 'jump':
      case 'fall':
        // Airborne held poses — static squash/stretch (no looping tween).
        this.sprite.setScale(peakX, peakY);
        break;
      case 'absorbing':
        // pon-absorb-hold (0.34s): continuous shake + lean, mouth pulses (texture).
        this.sprite.setScale(peakX, peakY);
        this.stateTween = this.scene.tweens.add({
          targets: this.sprite,
          x: { from: -1.5, to: 1.5 },
          angle: { from: -v.rotateDeg, to: v.rotateDeg },
          duration: v.durationMs / 2,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.InOut',
        });
        break;
      case 'absorbed':
        // Held / squished-content: a steady, slightly-flattened puff (no bob, no
        // shake) — matches the static `.pon-absorbed` CSS hold.
        this.sprite.setScale(peakX, peakY);
        break;
      case 'carrying':
        // Planted, slightly-squashed trudge (carrying gameplay = slow, no jump).
        this.sprite.setScale(peakX, peakY);
        this.stateTween = this.scene.tweens.add({
          targets: this.sprite,
          scaleY: { from: peakY, to: b * 0.99 },
          y: { from: baseY, to: baseY + v.liftPx },
          duration: v.durationMs,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.InOut',
        });
        break;
    }
  }

  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }

  /**
   * Reposition the name label vertically (px above the body centre, negative =
   * up) and set its opacity — used by the stage to stagger / fade labels so
   * clustered Pons stay legible (P2-D). `extraUp` adds to the base offset.
   */
  setLabelLayout(extraUp: number, alpha: number): void {
    const r = this.size / 2;
    this.label.setY(-r - 10 - extraUp);
    this.label.setAlpha(alpha);
  }

  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  destroy(): void {
    this.stateTween?.stop();
    this.scene.tweens.killTweensOf(this.sprite);
    this.container.destroy();
  }
}

/** Build a Pon character for `costume`. Convenience wrapper over the class. */
export function createPonCharacter(opts: PonCharacterOptions): PonCharacter {
  return new PonCharacter(opts);
}
